<script>

    import Map              from "./quotation/views/Map.svelte";
    import Itinerary        from "./quotation/views/Itinerary.svelte";
    import Hotels           from "./quotation/views/Hotels.svelte";
    import Restaurants      from "./quotation/views/Restaurants.svelte";
    import Monuments        from "./quotation/views/Monuments.svelte";
    import Rates            from "./quotation/views/Rates.svelte";
    import {Quotations}     from "../../class/business/Quotations";
    import Filemanager      from "../Filemanager/Filemanager.svelte";
    import {createEventDispatcher} from "svelte";
    import API from "../../class/API";
    import { t } from "../../i18n";
    import Deadlines        from "./quotation/views/Deadlines.svelte";
    import { collectDeadlines, worstLevel, LEVEL_STYLE } from "../../class/business/Deadlines";

    export let qid = null;

    $: lastUpdateLoading = false;
    $: quotationRow = null;

    let quotationPromise = Quotations.get(qid);

    let hasData = (arr) => arr && (arr.length || Object.values(arr).length);
    // keep the selected tab when the headers are re-rendered (e.g. after a language change)
    let isTabActive = (code, dflt) => { let el = typeof document !== 'undefined' && document.getElementById(code + '-tab'); return el ? el.classList.contains('active') : !!dflt; };
    let tabHead = (icon, code, title, active) => `
        <li class="nav-item">
            <a class="nav-link ${isTabActive(code, active)?'active':''}" id="${code}-tab" href="#${code}" data-toggle="tab" aria-controls="${code}" role="tab" aria-selected="false">
                <i class="bx ${icon} align-middle"></i><span class="align-middle">${title}</span>
            </a>
        </li>
    `;

    let downloadLastUpdate = function(quotationRow)
    {
        lastUpdateLoading = true;
        var quotationId = qid;

        API('quotation.files.dl.last', {id: quotationId}, {decoder: p=>p.blob(), parser: p=>p})
            .then(function(data){
                const a = document.createElement("a");
                a.style.display = "none";
                document.body.appendChild(a);
                a.href = window.URL.createObjectURL(new Blob([data]));
                let fileName = quotationRow ?
                    'update-'+quotationRow.date + '-' +(quotationRow.refid||'') + '-'  + (quotationRow.refid_remote||'').replace(/[^a-z0-9]/gi,'')+'.pdf':
                    'update-'+quotationId+'.pdf';
                a.setAttribute("download", fileName);
                a.click();
                window.URL.revokeObjectURL(a.href);
                document.body.removeChild(a);
            })
            .catch(()=>alert($t('common.download_error')))
            .finally(()=>lastUpdateLoading=false)
    };

    let dispatch = createEventDispatcher();
    quotationPromise.then(function(data)
    {
        quotationRow = data;
        setTimeout(function(){
            if(data) dispatch('quotationFetched', data);
        },0);
    });

</script>

<style>
    .nav-tabs > .nav-item {cursor:pointer;}
    .nav-link.loading{ pointer-events: none; opacity:.5; user-select:none; }
    /* quieter, colour-coded deadlines tab: no filled background, text/icon in the urgency colour */
    .nav-tabs > .nav-item.ml-auto .deadlines-tab-link {
        background: transparent; box-shadow: none;
        border: 1px solid var(--dl-tint, #E6EAEE); border-radius: 0.267rem;
        color: var(--dl-color, #475F7B); font-weight: 500; font-size: 0.9rem;
        padding: 0.45rem 0.85rem; margin-top: 0.1rem;
    }
    .nav-tabs > .nav-item.ml-auto .deadlines-tab-link:hover,
    .nav-tabs > .nav-item.ml-auto .deadlines-tab-link:focus,
    .nav-tabs > .nav-item.ml-auto .deadlines-tab-link.active {
        background: var(--dl-tint, #f2f4f4); color: var(--dl-color, #475F7B); border-color: transparent; box-shadow: none;
    }
    .nav-tabs > .nav-item.ml-auto .deadlines-tab-link i { color: inherit; }
    .nav-tabs > .nav-item.ml-auto .deadlines-tab-link .badge { font-size: 0.75em; }
</style>


<section id="basic-tabs-components">
    <div class="card">
        <div class="card-header">
            <div class="card-title">
                {#await quotationPromise then data}<h4>{$t('quotation.title', {ref: data.refid})}</h4>{/await}
            </div>
        </div>
        <div class="card-content">
            <div class="card-body">

                {#await quotationPromise}
                    <p>{$t('quotation.loading')}</p>

                {:then data}
                    {@const deadlines = collectDeadlines(data)}
                    <div>
                        <ul class="nav nav-tabs" role="tablist">
                            {@html tabHead('bx-map', 'map', $t('quotation.tabs.map'), true)}

                            {@html hasData(data.itinerary)      ? tabHead('bx-map-alt', 'itinerary', $t('quotation.tabs.itinerary')):''}
                            {@html hasData(data.hotels)         ? tabHead('bx-hotel', 'hotel', $t('quotation.tabs.hotels')):''}
                            {@html hasData(data.restaurants)    ? tabHead('bx-restaurant', 'restaurant', $t('quotation.tabs.restaurants')):''}
                            {@html hasData(data.monuments)      ? tabHead('bxs-landmark', 'monument', $t('quotation.tabs.monuments')):''}
                            {@html hasData(data.rates)          ? tabHead('bx-dollar', 'rates', $t('quotation.tabs.rates')):''}
                            {@html tabHead('bx-file', 'files', $t('quotation.tabs.files'))}

                            {#if data.updates && data.updates>0}
                                <li class="nav-item">
                                    <a class="nav-link active" href="javascript:void(0)" class:loading={lastUpdateLoading} data-toggle="tab" style="background: #23B04E; box-shadow: none;" on:click={()=>downloadLastUpdate(quotationRow)} >
                                        <i class="bx bxs-download align-middle"></i>
                                        <span class="align-middle">
                                            {$t('quotation.last_update')}
                                            {#if lastUpdateLoading}
                                                <span style="font-size:.86em;opacity:.75;"> {$t('quotation.loading_short')}</span>
                                            {/if}
                                        </span>
                                    </a>
                                </li>
                            {/if}

                            {#if deadlines.length}
                                {@const worst = worstLevel(deadlines)}
                                <li class="nav-item ml-auto">
                                    <a class="nav-link deadlines-tab-link" id="deadlines-tab" href="#deadlines" data-toggle="tab" aria-controls="deadlines" role="tab" aria-selected="false" title={$t('deadlines.title')}
                                       style="--dl-color: {LEVEL_STYLE[worst].color}; --dl-tint: {LEVEL_STYLE[worst].tint};">
                                        <i class="{LEVEL_STYLE[worst].icon} align-middle" class:bx-tada={worst === 'critical'}></i>
                                        <span class="align-middle">{$t('deadlines.tab')}</span>
                                        <span class="badge badge-pill {LEVEL_STYLE[worst].pill} align-middle ml-25">{deadlines.length}</span>
                                    </a>
                                </li>
                            {/if}

                        </ul>
                        <div class="tab-content">
                            <div class="tab-pane active" id="map" aria-labelledby="map-tab" style="text-align:center;"><Map {data}></Map></div>
                            <div class="tab-pane" id="itinerary" aria-labelledby="home-tab" style=""><Itinerary {data}></Itinerary></div>
                            <div class="tab-pane" id="hotel"><Hotels {data}></Hotels></div>
                            <div class="tab-pane" id="restaurant"><Restaurants {data}></Restaurants></div>
                            <div class="tab-pane" id="monument"><Monuments {data}></Monuments></div>
                            <div class="tab-pane" id="rates"><Rates {data}></Rates></div>
                            <div class="tab-pane" id="files"><Filemanager path="/Q{qid}"></Filemanager></div>
                            <div class="tab-pane" id="deadlines" aria-labelledby="deadlines-tab"><Deadlines {data}></Deadlines></div>
                        </div>
                    </div>


                {:catch err}
                    <p>{$t('quotation.error')}</p>

                {/await}

            </div>
        </div>
</section>

